import React, { useEffect, useRef, useState, useMemo } from 'react';
import Select from "react-select";
import { useTheme } from 'styled-components';
import ReactTooltip from 'react-tooltip'
import useMedia from 'use-media';
import useWindowSize from '../../../hooks/useWindowSize';
import SelectWrapper from '../../ui/forms/SelectWrapper';
import infoimage from '../../../assets/images/icons8-info-48.png';
import InputDisabled from '../../ui/forms/InputDisabled';
import TooltipImg from '../../ui/forms/TooltipImg';
import BoxedInputWrap from '../../ui/forms/BoxedInputWrap';

const dot = (color = '#ccc') => ({
    ':before': {
        backgroundColor: color,
    },
});

const getSingleValue = ref =>({
    children,
    getStyles,
    ...props
  }) => {
      const styles = getStyles('singleValue', {...props});
    return <div style={styles} ref={ref}>{children}</div>;
};


const BoxedSelect = (props) => {
    
    const { value, options, name, menuPlacement = 'bottom' } = props;
    const { color, color30 } = useTheme();

    const currentOption = useMemo(() => options.find(option => option.value === value), [value, options]) || null;

    const refValue = useRef({});
    const [isEllipsis, setIsEllipsis] = useState(false);
    const tooltipId = useMemo(() => `tooltip_id_${name}`, [name]);
    const tooltipText = useMemo(() => currentOption !== null ? currentOption.label : '', [currentOption]);

    const isWide = useMedia({ minWidth: '700px' });
    const windowSize = useWindowSize(!isWide && props.isDisabled);

    const styles = useMemo(() => ({
        control: styles => ({ 
            ...styles, 
            backgroundColor: 'white',
            height: '100%'
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? null
                    : isSelected
                        ? color
                        : isFocused
                            ? color30
                            : null,
                color: isSelected ? '#FFFFFF' : '#000000',
                cursor: isDisabled ? 'not-allowed' : 'default',
    
                ':active': {
                    ...styles[':active'],
                    backgroundColor: !isDisabled && (isSelected ? '#EB0A1E' : color30),
                },
            };
        },
        input: styles => ({ ...styles, ...dot() }),
        placeholder: styles => ({ ...styles, ...dot() }),
        singleValue: (styles, { data, isDisabled }) => 
        ({ 
            ...styles, 
            ...dot(data.color),
            maxWidth: 'calc(100%)',
            opacity: 1,
            color: isDisabled ? '#666666' : '#000000',
            WebkitTextFillColor: isDisabled ? '#666666' : '#000000'
         }),
        valueContainer: provided => ({
            ...provided,
            padding: 0
        }),
        dropdownIndicator: provided => ({ ...provided, marginLeft: '1rem', padding: 0 }),
    }), [color, color30]);

    const components = useMemo(() => ({
        IndicatorSeparator: () => null,
        SingleValue: getSingleValue(refValue)
    }),[tooltipText, tooltipId, isEllipsis, refValue]);

    useEffect(() => {
        const ellipsis =  !!(refValue.current && refValue.current.offsetHeight && refValue.current.scrollHeight && refValue.current.offsetWidth && refValue.current.scrollWidth ) && (refValue.current.offsetHeight < refValue.current.scrollHeight || refValue.current.offsetWidth < refValue.current.scrollWidth)
        setIsEllipsis(ellipsis);
    }, [refValue.current, windowSize.width, isWide, currentOption])
    
    useEffect(() => ReactTooltip.rebuild(), [isEllipsis]);

    return (
        <div> 
            <ReactTooltip effect="solid" html={false} place="top" id={tooltipId} />
            {
                props.isDisabled ? 
                <BoxedInputWrap disabled={props.isDisabled}>
                    <InputDisabled ref={refValue}>{tooltipText}</InputDisabled> 
                    {
                        options && isEllipsis && <TooltipImg src={infoimage} data-for={tooltipId} data-tip={tooltipText} />
                    }
                </BoxedInputWrap> : 
                <SelectWrapper>
                    <Select
                        styles={styles}
                        className="dropdownCreatable" 
                        classNamePrefix="react-select"
                        onChange={props.onChange}
                        isSearchable={false}
                        placeholder="Select"
                        menuPlacement={menuPlacement}
                        components={components}
                        name={props.name}
                        value={currentOption}
                        options={options} />
                </SelectWrapper>
            }
        </div>
    )
};

export default BoxedSelect;
